import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '../../features/auth/stores/authStore';
import { type EmployeeRole, EMPLOYEE_ROLES } from '../types/employee.types';

/** Set of valid EmployeeRole values for O(1) runtime validation */
const VALID_ROLES = new Set<string>(EMPLOYEE_ROLES);

/**
 * Protected route component that requires authentication.
 * Redirects to login page if user is not authenticated.
 */
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (!isAuthenticated) {
    // Redirect to login page - adjust the path as needed
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

interface RoleRouteProps {
  children: ReactNode;
  allowedRoles: EmployeeRole[];
}

/**
 * Role-based route guard component.
 * Requires authentication and one of the specified roles.
 * Redirects to /unauthorized if the current user's role is not allowed.
 */
export function RoleRoute({ children, allowedRoles }: RoleRouteProps) {
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Validate role is a known EmployeeRole before casting to avoid silent pass-through
  // of unknown JWT roles (e.g., future values, tampered tokens)
  if (!user || !VALID_ROLES.has(user.role) || !allowedRoles.includes(user.role as EmployeeRole)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
}

/**
 * Admin-only route component.
 * Requires authentication and SYSTEM_ADMIN role.
 */
export function AdminRoute({ children }: { children: ReactNode }) {
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'SYSTEM_ADMIN') {
    // Redirect to unauthorized page or home
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
}
